import { useCallback, useEffect, useRef, useState } from 'react';
import { listMicrophones, MICROPHONE_PREFERENCE_KEY, RecordingAudioSession, recordingSource } from './recordingAudio';
import { recordingProblem } from './mediaRecovery.mjs';
import { trackEvent } from './analytics';
import { trackRecordingDiagnostics } from './recordingDiagnostics';

export default function useRecordingAudio({ input, visible, recording = false }) {
  const [microphones, setMicrophones] = useState([]);
  const [microphoneId, setMicrophoneId] = useState(() => { try { return localStorage.getItem(MICROPHONE_PREFERENCE_KEY) || ''; } catch { return ''; } });
  const microphoneRef = useRef(microphoneId);
  const sessionRef = useRef(null), visibleRef = useRef(visible);
  const microphoneChangeRef = useRef(0);
  const lastDiagnosticsRef = useRef({}), recordingRef = useRef(recording);
  recordingRef.current = recording;
  visibleRef.current = visible;
  const [levels, setLevels] = useState({});
  const [ready, setReady] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [microphoneError, setMicrophoneError] = useState(false);
  const [testPhase, setTestPhase] = useState('idle');
  const [testResult, setTestResult] = useState(null);
  const [testUrl, setTestUrl] = useState('');
  const testRecorderRef = useRef(null), testTimerRef = useRef(null), testVersionRef = useRef(0), testUrlRef = useRef('');
  const testPlayerRef = useRef(null);

  const stopTest = useCallback(() => {
    testVersionRef.current++;
    clearTimeout(testTimerRef.current);
    try { testPlayerRef.current?.pause(); } catch {}
    const recorder = testRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') { try { recorder.stop(); } catch {} }
    testRecorderRef.current = null;
    setTestPhase('idle');
  }, []);

  const close = useCallback(() => {
    microphoneChangeRef.current++;
    stopTest();
    if (sessionRef.current) lastDiagnosticsRef.current = sessionRef.current.diagnostics();
    sessionRef.current?.close();
    sessionRef.current = null;
    if (testUrlRef.current) URL.revokeObjectURL(testUrlRef.current);
    testUrlRef.current = '';
    setTestUrl(''); setTestResult(null); setLevels({}); setReady(false); setPreparing(false); setSwitching(false);
  }, [stopTest]);

  const refreshMicrophones = useCallback(async () => {
    try {
      const devices = await listMicrophones();
      if (!visibleRef.current) return;
      setMicrophones(devices);
      // Before permission the list may intentionally hide every device ID.
      if (devices.some(device => device.label) && microphoneRef.current && !devices.some(device => device.deviceId === microphoneRef.current)) {
        microphoneRef.current = ''; setMicrophoneId('');
        try { localStorage.removeItem(MICROPHONE_PREFERENCE_KEY); } catch {}
      }
    } catch { /* Device enumeration is optional; the system default remains usable. */ }
  }, []);

  useEffect(() => {
    if (!visible) { close(); return; }
    refreshMicrophones();
    const devices = navigator.mediaDevices;
    devices?.addEventListener?.('devicechange', refreshMicrophones);
    return () => devices?.removeEventListener?.('devicechange', refreshMicrophones);
  }, [visible, close, refreshMicrophones]);
  useEffect(() => () => { visibleRef.current = false; close(); }, [close]);

  const prepare = useCallback(async () => {
    let session = sessionRef.current;
    if (!session || session.closed) {
      session = new RecordingAudioSession({ input, microphoneId: microphoneRef.current,
        onLevels: value => { if (visibleRef.current && sessionRef.current === session) setLevels(value); } });
      sessionRef.current = session;
      lastDiagnosticsRef.current = {};
    }
    setPreparing(true); setMicrophoneError(false);
    try {
      await session.prepare();
      if (!visibleRef.current || sessionRef.current !== session || session.closed) throw Object.assign(new Error('Recording cancelled'), { code: 'request_cancelled' });
      setReady(true);
      refreshMicrophones();
      return session;
    } finally { if (visibleRef.current && sessionRef.current === session) setPreparing(false); }
  }, [input, refreshMicrophones]);

  const changeMicrophone = async deviceId => {
    let version = ++microphoneChangeRef.current;
    const previous = microphoneRef.current;
    const session = sessionRef.current;
    setSwitching(true); setMicrophoneError(false);
    try { testPlayerRef.current?.pause(); } catch {}
    try {
      if (session && !session.closed) {
        if (session.canSwitchMicrophone) { if (await session.replaceMicrophone(deviceId) === false) return; }
        else { close(); version = ++microphoneChangeRef.current; }
      }
      if (!visibleRef.current || version !== microphoneChangeRef.current) return;
      microphoneRef.current = deviceId; setMicrophoneId(deviceId);
      try { if (deviceId) localStorage.setItem(MICROPHONE_PREFERENCE_KEY, deviceId); else localStorage.removeItem(MICROPHONE_PREFERENCE_KEY); } catch {}
      setTestResult(null);
      if (testUrlRef.current) URL.revokeObjectURL(testUrlRef.current);
      testUrlRef.current = ''; setTestUrl('');
      trackEvent('recording_microphone_changed', { source: recordingSource(input), during_capture: recordingRef.current,
        operation: recordingRef.current ? 'microphone_recording' : 'microphone_setup' });
    } catch (error) {
      if (!visibleRef.current || version !== microphoneChangeRef.current || error?.code === 'request_cancelled') return;
      microphoneRef.current = previous; setMicrophoneId(previous); setMicrophoneError(true);
      trackEvent('recording_microphone_change_error', { source: recordingSource(input), error_code: recordingProblem(error).code, operation: 'recording_microphone_change' });
    } finally { if (visibleRef.current && version === microphoneChangeRef.current) setSwitching(false); }
  };

  const startTest = async () => {
    stopTest(); const version = testVersionRef.current;
    setTestPhase('preparing'); setTestResult(null);
    if (testUrlRef.current) URL.revokeObjectURL(testUrlRef.current);
    testUrlRef.current = ''; setTestUrl('');
    try {
      const session = await prepare();
      if (testVersionRef.current !== version) return;
      session.resetSignals();
      const types = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4;codecs=mp4a.40.2'];
      const mimeType = types.find(type => MediaRecorder.isTypeSupported(type));
      const recorder = new MediaRecorder(session.outputStream, { ...(mimeType ? { mimeType } : {}), audioBitsPerSecond: 24000 });
      const chunks = [];
      recorder.ondataavailable = event => { if (event.data.size) chunks.push(event.data); };
      recorder.onstop = () => {
        if (!visibleRef.current || testVersionRef.current !== version) return;
        const result = session.diagnostics(); setTestResult(result); setTestPhase('complete');
        session.setPaused(true);
        if (testUrlRef.current) URL.revokeObjectURL(testUrlRef.current);
        testUrlRef.current = URL.createObjectURL(new Blob(chunks, { type: recorder.mimeType || mimeType }));
        setTestUrl(testUrlRef.current);
        trackEvent('recording_audio_check_complete', { source: recordingSource(input), ...result });
        trackRecordingDiagnostics('recording_audio_check', result);
      };
      testRecorderRef.current = recorder;
      recorder.start(); setTestPhase('testing');
      trackEvent('recording_audio_check_started', { source: recordingSource(input) });
      testTimerRef.current = setTimeout(() => { if (recorder.state !== 'inactive') recorder.stop(); }, 6000);
    } catch (error) { if (testVersionRef.current === version) setTestPhase('idle'); throw error; }
  };

  const setPaused = useCallback(paused => sessionRef.current?.setPaused(paused), []);
  const diagnostics = useCallback(() => sessionRef.current?.diagnostics() || lastDiagnosticsRef.current, []);

  return { microphones, microphoneId, levels, ready, preparing, switching, microphoneError,
    testPhase, testResult, testUrl, testPlayerRef, prepare, close, stopTest, startTest, changeMicrophone,
    canSwitchMicrophone: Boolean(sessionRef.current?.canSwitchMicrophone),
    setPaused, diagnostics };
}
